import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import GenericModel from '../model/generic-model';
import { SnackService } from './snack.service';
import { LoadingService } from './loading.service';
import { Router } from '@angular/router';
import ErrorHelper from '../helpers/error-helper';
import * as _ from 'lodash';

export const DEBOUNCE_TIME = 2000;
export const SHORT_DEBOUNCE_TIME = 1000;
export const SHORTER_DEBOUNCE_TIME = 500;
export const NOT_FOUND = 'NOT_FOUND';

@Injectable({
  providedIn: 'root',
})
export class RequestService {
  constructor(
    public http: HttpClient,
    private snackService: SnackService,
    private loadingService: LoadingService,
    private router: Router
  ) {}

  handleError(
    error: HttpErrorResponse,
    showWarningMessage = true,
    showErrorMessage = true
  ) {
    this.loadingService.savingFailed();
    console.error(error);
    let errMsg = 'Er is iets misgegaan, probeer later opnieuw.';
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.message);
    } else {
      console.error(
        `Backend returned code ${error.status}, ` + `body was: ${error.message}`
      );
      if (ErrorHelper.isConnectionLostError(error)) {
        if (showWarningMessage) {
          this.snackService.showWarning('Kon server niet bereiken');
        }
        return throwError(error);
      }
      if (error.status === 404) {
        if (showWarningMessage) {
          this.snackService.showWarning('Niets gevonden.');
        }
        return throwError(NOT_FOUND);
      }
      if (error.status && error.status !== 504 && error.status !== 400) {
        if (
          error.error &&
          typeof error.error === 'string' &&
          error.status !== 413
        ) {
          errMsg = `Error: ${error.error}`;
        } else if (error.status === 413) {
          errMsg = 'Request overschreed limiet op grootte';
        } else {
          errMsg = `Error: ${error.message}`;
        }
      }
      if (
        error != null &&
        error.error != null &&
        (error.error.includes('Kon niet toevoegen aan Teamleader') ||
          error.error.includes('Kon niet bewerken in Teamleader'))
      ) {
        return throwError(error.error);
      }
      if (error.status === 401) {
        localStorage.removeItem('inpress_access_token');
        this.router.navigateByUrl('/login');
      }
    }
    if (showErrorMessage) {
      this.snackService.showError(errMsg);
    }
    return throwError(error);
  }

  get(
    link: string,
    params?: any,
    sort?: string,
    order?: string,
    size?: number,
    page?: number,
    filters?: string,
    otherSorts?: PageableSort[],
    showWarningMessage = true,
    showErrorMessage = true
  ): Observable<HttpResponse<any>> {
    link = this.filterEndpoint(link);
    let httpParams = new HttpParams();
    if (params && params !== '') {
      params.forEach(function (param) {
        httpParams = httpParams.set(param['key'], param['value']);
      });
    }
    if (!sort) {
      sort = 'id';
    }
    if (!order) {
      order = 'asc';
    }

    let otherSortsParam = '';
    if (otherSorts != null && otherSorts.length > 0) {
      otherSorts.forEach(
        (s) => (otherSortsParam += '&sort=' + s.key + ',' + s.order)
      );
    }

    let url = `/api${link}?sort=${sort},${order}&size=${size}&page=${page}`;
    if (filters) {
      url += `&filters=${filters}`;
    }
    if (otherSortsParam !== '') {
      url += otherSortsParam;
    }
    return this.http
      .get<HttpResponse<any>>(url, {
        observe: 'response',
        params: httpParams,
      })
      .pipe(
        catchError(
          _.bind(
            this.handleError,
            this,
            _,
            showWarningMessage,
            showErrorMessage
          )
        )
      );
  }

  getNormal(link: string): Observable<any> {
    link = this.filterEndpoint(link);
    let httpParams = new HttpParams();

    return this.http
      .get<any>(`/api${link}`, {
        params: httpParams,
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  getIndividual(link: string, params?: any): Observable<any> {
    link = this.filterEndpoint(link);
    let httpParams = new HttpParams();
    if (params && params !== '') {
      if (typeof params === 'object') {
        httpParams = params;
      } else {
        params.forEach(function (param) {
          httpParams = httpParams.set(param['key'], param['value']);
        });
      }
    }
    return this.http
      .get(`/api${link}`, {
        params: httpParams,
        observe: 'response',
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  post(
    body: any,
    path: string,
    contentType?: string,
    showWarningMessage = true
  ): Observable<any> {
    path = this.filterEndpoint(path);
    return this.http
      .post(`/api${path}`, body)
      .pipe(catchError(_.bind(this.handleError, this, _, showWarningMessage)));
  }

  postFetch(
    body: any,
    path: string,
    sort?: string,
    order?: string,
    size?: number,
    page?: number,
    filters?: string
  ): Observable<HttpResponse<any>> {
    path = this.filterEndpoint(path);
    if (!sort) {
      sort = 'id';
    }
    if (!order) {
      order = 'asc';
    }

    let url = `/api${path}?sort=${sort},${order}&size=${size}&page=${page}`;
    if (filters) {
      url += `&filters=${filters}`;
    }
    return this.http
      .post<HttpResponse<any>>(url, body, {
        observe: 'response',
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  put(object: GenericModel, path: string): Observable<any> {
    path = this.filterEndpoint(path);
    if (object) {
      return this.http
        .put(`/api${path}/${object.id}`, object)
        .pipe(catchError(this.handleError.bind(this)));
    }
  }

  putPart(body: any, path: string, id: number): Observable<any> {
    path = this.filterEndpoint(path);
    if (body) {
      return this.http
        .put(`/api${path}/${id}`, body)
        .pipe(catchError(this.handleError.bind(this)));
    }
  }

  putCustom(body: any, path: string): Observable<any> {
    path = this.filterEndpoint(path);
    if (body) {
      return this.http
        .put(`/api${path}`, body)
        .pipe(catchError(this.handleError.bind(this)));
    }
  }

  delete(link: string): Observable<any> {
    link = this.filterEndpoint(link);
    return this.http
      .delete(`/api${link}`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  filterEndpoint(endpoint: string): string {
    if (endpoint.trim().charAt(0) !== '/') {
      endpoint = '/' + endpoint;
    }
    return endpoint;
  }
}

export interface PageableSort {
  key: string;
  order: string;
}
